import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import CustomButton from 'components/Buttons/CustomButton'
import LoadingV2 from 'components/LoadingV2'
import PMBModalImagePreview from 'components/PMBModalImagePreview'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import { TableComponent as DataTable } from 'components/TableComponent'
import { useDebounce } from 'helpers'
import { useSnackbar } from 'notistack'
import { useEffect, useState, useMemo, useRef } from 'react'
import {
  ArrowLeft,
  ChevronDown,
  Filter,
  ChevronUp,
  X,
} from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  CardBody,
  Spinner,
  Badge,
  Col,
  Collapse,
  Row,
} from 'reactstrap'
import moment from 'moment-timezone'
import { PMBDateRangePicker } from 'components/PMBNewDatePicker'
import PMBSelect from 'components/PMBSelect'
import { formatDateOnlyPMBV1 } from 'utils/formatDate'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_FINDORA_LOGS, URL_WORKPLACE } from 'utils/pathUrl'
import {
  useSyncFindoraEmployeesMutation,
  useSyncFindoraItemsMutation,
} from '../sync-data/syncDataListApiSlice'
import ModalAddProcess from './ModalAddProcess'
import ModalGroupMapping from './ModalGroupMapping'
import { useFetchFindoraResultInventoryListQuery } from './productMappingSchemaListApiSlice'
import {
  clearSelectedItems,
  setFetchOption,
  toggleAllSelectedItems,
  toggleSelectedItem,
} from './productMappingSchemaListSlice'
import SvgIcon from 'components/Icon/SvgIcon'

const ListProductMappingSchema = () => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const { tag, selectedItems } = useSelector(
    (state) => state.productMappingSchemaList,
  )

  const [searchInput, setSearchInput] = useState('')
  const [isFilter, setIsFilter] = useState(false)
  const [imageItems] = useState([])
  const [modalOpenImage, setModalOpenImage] = useState(false)
  const [isOpenModalAddProcess, setIsOpenModalAddProcess] = useState(false)
  const [isOpenModalMapping, setIsOpenModalMapping] = useState(false)
  const [dataProcessById, setDataProcessById] = useState(null)
  const [isSync, setIsSync] = useState(false)

  const firstDay = moment().subtract(3, 'months').format('YYYY-MM-DD')
  const lastDay = moment().format('YYYY-MM-DD')

  const [selectedDate, setSelectedDate] = useState([
    tag?.start !== undefined ? tag?.start : firstDay,
    tag?.end !== undefined ? tag?.end : lastDay,
  ])
  const defaultDateRangeRef = useRef([firstDay, lastDay])
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const mappedOptions = [
    { label: 'Semua', value: '' },
    { label: 'Sudah Dipetakan', value: '1' },
    { label: 'Belum Dipetakan', value: '0' },
  ]
  const hasSchemaOptions = [
    { label: 'Semua', value: '' },
    { label: 'Tersedia', value: '1' },
    { label: 'Tidak Tersedia', value: '0' },
  ]

  const isDateFilterChanged = useMemo(() => {
    return (
      selectedDate === null ||
      selectedDate.length === 0 ||
      (Array.isArray(selectedDate) &&
        (moment(selectedDate[0]).format('YYYY-MM-DD') !==
          moment(defaultDateRangeRef.current[0]).format('YYYY-MM-DD') ||
          moment(selectedDate[1]).format('YYYY-MM-DD') !==
            moment(defaultDateRangeRef.current[1]).format('YYYY-MM-DD')))
    )
  }, [selectedDate])

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (tag?.start || tag?.end) count++
    if (tag?.mapped !== undefined && tag.mapped !== '') count++
    if (tag?.hasSchema !== undefined && tag.hasSchema !== '') count++
    if (searchInput && searchInput.trim().length > 0) count++
    return count
  }, [isDateFilterChanged, searchInput, tag])

  const handleClearFilters = () => {
    setSelectedDate([])
    setSearchInput('')
    dispatch(clearSelectedItems())
    dispatch(
      setFetchOption({
        size: 10,
        page: 0,
        s: '',
        start: '',
        end: '',
        mapped: '',
        hasSchema: '',
      }),
    )
  }

  const handleFilterChange = (value, type) => {
    dispatch(
      setFetchOption({
        page: 0,
        [type]: value,
      }),
    )
  }

  const { data, isLoading, refetch, isFetching } =
    useFetchFindoraResultInventoryListQuery(
      {
        page: tag?.page ?? 0,
        size: tag?.size ?? 10,
        s: tag?.s,
        sortby: tag?.sortby ?? 'createdAt',
        sortdir: tag?.sortdir,
        from: (
          tag?.start !== undefined
            ? tag.start
            : Array.isArray(selectedDate)
              ? selectedDate[0]
              : undefined
        )
          ? moment(
              tag?.start !== undefined
                ? tag.start
                : Array.isArray(selectedDate)
                  ? selectedDate[0]
                  : undefined,
            )
              .startOf('day')
              .toISOString()
          : undefined,
        to: (
          tag?.end !== undefined
            ? tag.end
            : Array.isArray(selectedDate)
              ? selectedDate[1]
              : undefined
        )
          ? moment(
              tag?.end !== undefined
                ? tag.end
                : Array.isArray(selectedDate)
                  ? selectedDate[1]
                  : undefined,
            )
              .endOf('day')
              .toISOString()
          : undefined,
        ...(tag?.mapped !== undefined &&
          tag?.mapped !== '' && { mapped: tag.mapped }),
        ...(tag?.hasSchema !== undefined &&
          tag?.hasSchema !== '' && { hasSchema: tag.hasSchema }),
      },
      { refetchOnMountOrArgChange: true, skip: false },
    )

  useEffect(() => {
    return () => {
      dispatch(clearSelectedItems())
    }
  }, [dispatch])

  const selectedItemsArray = Object.values(selectedItems || {}).filter(Boolean)
  const totalItemsSelected = selectedItemsArray.length

  const activeProcessId =
    selectedItemsArray.length > 0
      ? selectedItemsArray[0].product_process_id
      : null

  const selectableItems =
    data?.items?.filter(
      (item) => item?.possibleItems?.length > 0 && !item?.inventory?.id,
    ) || []
  const targetProcessId =
    activeProcessId ||
    (selectableItems.length > 0 ? selectableItems[0].product_process_id : null)
  const validSelectableItems = selectableItems.filter(
    (item) => item.product_process_id === targetProcessId,
  )

  const totalQuantity = selectedItemsArray.reduce(
    (acc, item) => acc + Number(item?.quantity || 0),
    0,
  )
  const totalQuantityStr = newFormatThousandSeparator(totalQuantity, 3)


  // NEW COLUMNS
  const columns = [
    // CHECKBOX
    {
      name: (
        <div className="d-none d-md-block">
          <input
            type="checkbox"
            onChange={(e) => {
              const isChecked = e.target.checked
              dispatch(
                toggleAllSelectedItems({
                  items: validSelectableItems,
                  isSelected: isChecked,
                }),
              )
            }}
            checked={
              validSelectableItems.length > 0 &&
              validSelectableItems.every((item) => !!selectedItems[item.id])
            }
          />
        </div>
      ),
      width: '50px',
      cell: (row) => {
        const isSelectable =
          row?.possibleItems?.length > 0 && !row?.inventory?.id
        const isProcessIdMatch = activeProcessId
          ? row.product_process_id === activeProcessId
          : true
        const isDisabled =
          !isSelectable || (!isProcessIdMatch && !selectedItems[row.id])
        return (
          <div
            className="d-flex align-items-start justify-content-start"
            style={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              width: '100%',
              height: '100%',
              padding: '10px 0',
            }}
            onClick={() => {
              if (!isDisabled) {
                dispatch(
                  toggleSelectedItem({
                    item: row,
                    isSelected: !selectedItems[row.id],
                  }),
                )
              }
            }}
          >
            <input
              type="checkbox"
              disabled={isDisabled}
              checked={!!selectedItems[row.id]}
              onChange={(e) => {
                dispatch(
                  toggleSelectedItem({
                    item: row,
                    isSelected: e.target.checked,
                  }),
                )
              }}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
            />
          </div>
        )
      },
    },
    {
      name: <span>Tanggal Kerja</span>,
      selector: 'createdAt',
      grow: 2,
      sortable: true,
      sortField: 'createdAt',
      cell: (row) => {
        return (
          <span>
            {row?.createdAt ? formatDateOnlyPMBV1(row?.createdAt) : '-'}
          </span>
        )
      },
      minWidth: '150px',
    },
    {
      name: <span>Nama Karyawan</span>,
      minWidth: '200px',
      selector: 'employee_identity_number',
      grow: 2,
      // sortable: true,
      // sortField: 'employee_identity_number',
      cell: (row) => {
        return (
            <div className="d-flex flex-column">
            <span className="fw-bold text-primary m-0" data-testid={`product-mapping-schema-row-${row?.id}`}>
              {row?.employee_name}
            </span>
            {row?.employee_identity_number && (
              <span className="text-muted">
                {row?.employee_identity_number}
              </span>
            )}
          </div>
        )
      },
    },
    {
      name: <span>Nama Proses</span>,
      minWidth: '200px',
      selector: 'product_process_name',
      grow: 2,
      cell: (row) => {
        return <span>{row?.product_process_name ?? '-'}</span>
      },
    },
    {
      name: <span>Nama Barang</span>,
      minWidth: '150px',
      selector: 'inventory.item.nameItem',
      grow: 2,
      cell: (row) => {
        return <span>{row?.inventory?.item?.nameItem ?? '-'}</span>
      },
    },
    {
      name: <span>Jumlah</span>,
      minWidth: '100px',
      selector: 'quantity',
      grow: 2,
      right: true,
      cell: (row) => {
        return <span>{newFormatThousandSeparator(row?.quantity ?? 0, 3)}</span>
      },
    },
    {
      name: <span>Ketersediaan Skema</span>,
      minWidth: '150px',
      selector: 'possibleItems',
      grow: 2,
      cell: (row) => {
        return (
          <div>
            {row?.possibleItems?.length > 0 ? (
              <div
                className="badge rounded-pill bg-success-50 text-success px-2 flex-centered gap-1"
                style={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  maxWidth: '100%',
                }}
              >
                Tersedia
              </div>
            ) : (
              <div
                className="badge rounded-pill bg-danger-50 text-danger px-2 flex-centered gap-1"
                style={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  maxWidth: '100%',
                }}
              >
                Tidak Tersedia
              </div>
            )}
          </div>
        )
      },
    },
    {
      name: '',
      width: '150px',
      cell: (row) => {
        return (
          <CustomButton
            disabled={row?.inventory?.id}
            color="primary"
            testId={`product-mapping-schema-pemetaan-button-${row?.id}`}
            onClick={() => {
              setIsOpenModalAddProcess(true)
              setDataProcessById(row)
            }}
          >
            <P size="small">Pemetaan</P>
          </CustomButton>
        )
      },
    },
  ]

  const renderMobileCard = (row, index) => {
    const isSelectable = row?.possibleItems?.length > 0 && !row?.inventory?.id
    const isProcessIdMatch = activeProcessId
      ? row.product_process_id === activeProcessId
      : true
    const isDisabled =
      !isSelectable || (!isProcessIdMatch && !selectedItems[row.id])

    return (
      <Card
        key={index}
        className="mb-3 border-0 shadow-sm"
        style={{ borderRadius: '12px', overflow: 'hidden' }}
      >
        <CardBody className="p-0">
          {/* Header Section */}
          <div
            className="p-3 border-bottom d-flex justify-content-between align-items-center"
            style={{ backgroundColor: '#F9FAFB' }}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                onClick={() => {
                  if (!isDisabled) {
                    dispatch(
                      toggleSelectedItem({
                        item: row,
                        isSelected: !selectedItems[row.id],
                      }),
                    )
                  }
                }}
                style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
              >
                <input
                  type="checkbox"
                  disabled={isDisabled}
                  checked={!!selectedItems[row.id]}
                  onChange={(e) => {
                    dispatch(
                      toggleSelectedItem({
                        item: row,
                        isSelected: e.target.checked,
                      }),
                    )
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                />
              </div>
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                {row?.createdAt ? formatDateOnlyPMBV1(row?.createdAt) : '-'}
              </div>
            </div>
            <div>
              {row?.possibleItems?.length > 0 ? (
                <div
                  className="badge rounded-pill bg-success-50 text-success px-2"
                  style={{ fontSize: '0.75rem' }}
                >
                  Tersedia
                </div>
              ) : (
                <div
                  className="badge rounded-pill bg-danger-50 text-danger px-2"
                  style={{ fontSize: '0.75rem' }}
                >
                  Tidak Tersedia
                </div>
              )}
            </div>
          </div>

          {/* Content Section */}
          <div className="p-3">
            {/* Employee Info */}
            <div className="mb-3 pb-3 border-bottom">
              <div className="fw-bold text-primary" style={{ fontSize: '1rem' }}>
                {row?.employee_name || '-'}
              </div>
              <div className="text-muted small">
                {row?.employee_identity_number || '-'}
              </div>
            </div>

            {/* Process & Commodity Details */}
            <div className="row g-3 mb-3">
              <div className="col-6">
                <div
                  className="text-muted mb-1 text-uppercase"
                  style={{ fontSize: '0.65rem', letterSpacing: '0.025em' }}
                >
                  Nama Proses
                </div>
                <div className="fw-medium text-dark" style={{ fontSize: '0.85rem' }}>
                  {row?.product_process_name || '-'}
                </div>
              </div>
              <div className="col-6">
                <div
                  className="text-muted mb-1 text-uppercase"
                  style={{ fontSize: '0.65rem', letterSpacing: '0.025em' }}
                >
                  Nama Barang
                </div>
                <div className="fw-medium text-dark" style={{ fontSize: '0.85rem' }}>
                  {row?.inventory?.item?.nameItem || '-'}
                </div>
              </div>
            </div>

            {/* Quantity & Action */}
            <div className="d-flex justify-content-between align-items-end mt-2">
              <div>
                <div
                  className="text-muted mb-1 text-uppercase"
                  style={{ fontSize: '0.65rem', letterSpacing: '0.025em' }}
                >
                  Jumlah
                </div>
                <div className="fw-bold text-dark" style={{ fontSize: '1.25rem' }}>
                  {newFormatThousandSeparator(row?.quantity ?? 0, 3)}
                </div>
              </div>
              <CustomButton
                disabled={row?.inventory?.id}
                color="primary"
                onClick={() => {
                  setIsOpenModalAddProcess(true)
                  setDataProcessById(row)
                }}
                className="px-3"
                style={{ height: '36px' }}
              >
                <P size="small" className="mb-0">Pemetaan</P>
              </CustomButton>
            </div>
          </div>
        </CardBody>
      </Card>
    )
  }

  const handleSort = (column, direction) => {
    dispatch(
      setFetchOption({
        sortby: column.sortField,
        sortdir: direction?.toUpperCase(),
      }),
    )
  }

  useEffect(() => {
    dispatch(setFetchOption({ page: 0 }))
  }, [searchInput, dispatch])

  const debouncedSearch = useDebounce((value) => {
    dispatch(
      setFetchOption({
        page: 0,
        s: value,
      }),
    )
  }, 600)

  const onHandleSearchChange = (value) => {
    if (!value?.trim()) {
      setIsFilter(false)
    } else {
      setIsFilter(true)
    }
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && tag?.s.length === 0) {
      e.preventDefault()
    }
  }

  // SYNC
  const [syncFindoraItems] = useSyncFindoraItemsMutation()
  const [syncFindoraEmployees] = useSyncFindoraEmployeesMutation()
  const handleSyncData = async () => {
    try {
      setIsSync(true)
      const syncFindoraItemsResponse = await syncFindoraItems()
      const syncFindoraEmployeesResponse = await syncFindoraEmployees()
      // SYNC ITEMS
      if (syncFindoraItemsResponse?.error?.data?.message) {
        throw syncFindoraItemsResponse?.error?.data?.message
      } else {
        enqueueSnackbar('Data item berhasil disinkronisasi', {
          variant: 'success',
        })
      }
      // SYNC EMPLOYEES
      if (syncFindoraEmployeesResponse?.error?.data?.message) {
        throw syncFindoraEmployeesResponse?.error?.data?.message
      } else {
        enqueueSnackbar('Data karyawan berhasil disinkronisasi', {
          variant: 'success',
        })
      }
      dispatch(setFetchOption({ page: 0, s: undefined }))
      setSearchInput('')
      refetch()
    } catch (error) {
      enqueueSnackbar(error || 'Gagal sinkronisasi data', {
        variant: 'error',
      })
      dispatch(setFetchOption({ page: 0, s: undefined }))
      setSearchInput('')
      refetch()
    } finally {
      setIsSync(false)
    }
  }

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      {/* MODAL IMAGE PREVIEW */}
      <PMBModalImagePreview
        isOpen={modalOpenImage}
        toggle={() => setModalOpenImage(!modalOpenImage)}
        images={imageItems}
      />

      {/* MODAL ADD PROCESS */}
      <ModalAddProcess
        isOpen={isOpenModalAddProcess}
        toggle={() => {
          setIsOpenModalAddProcess(false)
          setDataProcessById(null)
        }}
        onSuccess={() => {
          dispatch(clearSelectedItems())
          refetch()
        }}
        clearSelectedItems={() => dispatch(clearSelectedItems())}
        // dataExistingConfiguration={data || []}
        dataProcessById={dataProcessById}
      />

      {/* MODAL MAPPING SEKALIGUS */}
      <ModalGroupMapping
        isOpen={isOpenModalMapping}
        toggle={() => setIsOpenModalMapping(false)}
        onSuccess={() => {
          dispatch(clearSelectedItems())
          refetch()
        }}
        selectedItems={selectedItems}
        clearSelectedItems={() => dispatch(clearSelectedItems())}
      />

      <div className="mt-3">
        <BreadCrumbs
          breadCrumbParent={'Alat Bantu'}
          breadCrumbParentLink={URL_WORKPLACE}
          breadCrumbActive="Daftar Pemetaan Bahan Baku"
        />
      </div>

      <Card className="mb-3 border-0 shadow-sm">
        <>
          <div className="row align-items-center py-3 mx-1">
            <div className="col-12 col-md-5 col-xl-4 mb-3 mb-md-0 d-flex align-items-center justify-content-start">
              <h5 className="fw-semibold mb-0">Daftar Pemetaan Bahan Baku</h5>
            </div>

            <div className="col-12 col-md-7 col-xl-8 d-flex flex-wrap justify-content-start justify-content-md-end gap-2 mt-2 mt-md-0">
              <Button
                outline
                color="primary"
                data-testid="product-mapping-schema-history-button"
                className="d-flex align-items-center justify-content-center text-nowrap"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(URL_FINDORA_LOGS)}
              >
                <ArrowLeft size={16} className="me-1" />
                Riwayat Sinkronisasi
              </Button>

              <Button
                color="primary"
                data-testid="product-mapping-schema-sync-button"
                className="d-flex align-items-center justify-content-center text-nowrap"
                style={{ cursor: 'pointer' }}
                onClick={handleSyncData}
                disabled={isSync}
              >
                {isSync ? (
                  <Spinner size="sm" className="me-2" />
                ) : (
                  <SvgIcon name={"old-icons-refresh"} size={16} className="me-2" color={"white"} />
                )}
                Sinkronisasi
              </Button>
              <Button
                color="primary"
                data-testid="product-mapping-schema-bulk-button"
                className="d-flex align-items-center justify-content-center text-nowrap"
                style={{ cursor: 'pointer' }}
                onClick={() => setIsOpenModalMapping(true)}
                disabled={totalItemsSelected === 0}
              >
                <SvgIcon name={"report-converted"} size={16} className="me-2" color={"white"}/>
                Pemetaan Sekaligus
              </Button>
            </div>
          </div>


          <div className="mx-3 d-flex flex-column gap-3 mb-3">
            <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3 w-100">
              <div className="d-flex align-items-center gap-2">
                {activeFilterCount > 0 && (
                  <CustomButton
                    onClick={handleClearFilters}
                    color="danger"
                    testId="product-mapping-schema-clear-filter-button"
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: '40px',
                      height: '40px',
                      padding: 0,
                      minWidth: '40px',
                    }}
                  >
                    <X size={16} />
                  </CustomButton>
                )}
                <CustomButton
                  color="primary"
                  outline
                  className="flex-grow-1 position-relative"
                  testId="product-mapping-schema-filter-button"
                  onClick={() => setIsFilterOpen((prev) => !prev)}
                >
                  <div className="d-flex align-items-center gap-2 justify-content-center">
                    <Filter size={16} />
                    <span>Filter</span>
                    {activeFilterCount > 0 && (
                      <Badge
                        color="danger"
                        pill
                        style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          fontSize: '10px',
                        }}
                      >
                        {activeFilterCount}
                      </Badge>
                    )}
                    {isFilterOpen ? (
                      <ChevronUp size={16} className="ml-2" />
                    ) : (
                      <ChevronDown size={16} className="ml-2" />
                    )}
                  </div>
                </CustomButton>
              </div>
            </div>

            <Collapse isOpen={isFilterOpen}>
              <div
                className="border rounded p-3 bg-light"
                style={{
                  width: '100%',
                  transition: 'all 0.3s ease-in-out',
                  opacity: isFilterOpen ? 1 : 0,
                  transform: isFilterOpen
                    ? 'translateY(0)'
                    : 'translateY(-10px)',
                }}
              >
                <Row className="g-2">
                  <Col xs={12} md={4} lg={3}>
                    <p className="mb-2 text-sm text-dark font-weight-bold">
                      Cari
                    </p>
                    <PMBSearchInputGroup
                      value={searchInput}
                      testId="product-mapping-schema-search-input"
                      placeholder="Cari..."
                      onChange={(e) => onHandleSearchChange(e.target.value)}
                      maxWidth="100%"
                      onKeyDown={handleKeyDown}
                    />
                  </Col>
                  <Col xs={12} md="auto" lg="auto">
                    <PMBDateRangePicker
                      data-testid="product-mapping-schema-date-range"
                      style={{ width: '100%', maxWidth: '100%' }}
                      value={selectedDate ?? []}
                      onChange={(dates) => {
                        if (dates?.length === 2) {
                          setSelectedDate(dates)
                          dispatch(
                            setFetchOption({
                              start: dates[0],
                              end: dates[1],
                            }),
                          )
                        }
                      }}
                      onClear={() => {
                        setSelectedDate([])
                        dispatch(
                          setFetchOption({
                            start: '',
                            end: '',
                          }),
                        )
                      }}
                      format="yyyy-mm-dd"
                      clearMode="callback"
                    />
                  </Col>
                  <Col xs={12} md={4} lg={3}>
                    <p className="mb-2 text-sm text-dark font-weight-bold">
                      Status Pemetaan
                    </p>
                    <PMBSelect
                      testId="product-mapping-schema-mapped-select"
                      style={{ width: '100%' }}
                      placeholder="Semua"
                      options={mappedOptions}
                      onChange={(e) => handleFilterChange(e?.value, 'mapped')}
                      value={mappedOptions.find(
                        (e) => e.value === (tag?.mapped ?? ''),
                      )}
                      menuPortalTarget={
                        typeof window !== 'undefined' ? document.body : null
                      }
                    />
                  </Col>

                  <Col xs={12} md={4} lg={3}>
                    <p className="mb-2 text-sm text-dark font-weight-bold">
                      Ketersediaan Skema
                    </p>
                    <PMBSelect
                      testId="product-mapping-schema-has-schema-select"
                      style={{ width: '100%' }}
                      placeholder="Semua"
                      options={hasSchemaOptions}
                      onChange={(e) =>
                        handleFilterChange(e?.value, 'hasSchema')
                      }
                      value={hasSchemaOptions.find(
                        (e) => e.value === (tag?.hasSchema ?? ''),
                      )}
                      menuPortalTarget={
                        typeof window !== 'undefined' ? document.body : null
                      }
                    />
                  </Col>
                </Row>
              </div>
            </Collapse>
          </div>
        </>

        <CardBody>
          <DataTable
            responsive
            persistTableHead
            renderMobileCard={renderMobileCard}
            defaultSortFieldId={1}
            defaultSortAsc={false}
            columns={columns}
            data={data?.items || []}
            progressPending={isFetching}
            paginationTotalRows={data?.totalItems}
            pagination
            paginationServer
            paginationComponent={() => (
              <PMBPagination
                rowCount={data?.totalItems || 0}
                currentPage={tag?.page}
                rowsPerPage={tag?.size}
                setCurrentPage={(e) => {
                  dispatch(setFetchOption({ page: e }))
                }}
                setRowsPerPage={(e) => {
                  dispatch(setFetchOption({ size: e }))
                }}
              />
            )}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner />
              </div>
            }
            onSort={handleSort}
            sortIcon={<ChevronDown />}
            sortServer
            noDataComponent={
              <p className="my-3 fw-semibold">
                {isFilter
                  ? 'Tidak ada data yang sesuai dengan pencarian Anda'
                  : 'Belum ada daftar proses pemetaan schema yang tersedia'}
              </p>
            }
          />
        </CardBody>
      </Card>

      {/* FLOATING ACTION BAR */}
      {totalItemsSelected > 0 && (
        <div
          className="position-fixed bottom-0 start-50 translate-middle-x mb-4 shadow-lg rounded bg-white px-4 py-3 d-flex align-items-center justify-content-between flex-wrap gap-3"
          style={{
            width: '90%',
            maxWidth: '800px',
            zIndex: 1030,
            border: '1px solid #e0e0e0',
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <div>
              <span className="fw-bold text-primary">Terpilih:</span>{' '}
              {totalItemsSelected} Items (Total: {totalQuantityStr})
            </div>
            <Button
              color="link"
              size="sm"
              className="text-danger p-0 ms-2 text-decoration-none"
              onClick={() => dispatch(clearSelectedItems())}
            >
              Batal Pilih
            </Button>
          </div>
          <Button
            color="primary"
            data-testid="product-mapping-schema-floating-bulk-button"
            className="d-flex align-items-center"
            onClick={() => setIsOpenModalMapping(true)}
          >
            <SvgIcon name={"report-converted"} size={16} className="me-2" color={"white"}/>
            Pemetaan Sekaligus
          </Button>
        </div>
      )}
    </div>
  )
}

export default ListProductMappingSchema
